import React from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { ConfirmActionDialog } from './ConfirmActionDialog';

export interface CustomContentManagerProps<TItem extends { id: string }, TForm> {
  /** Singular label used for buttons and form titles, e.g. "Creature". */
  itemLabel: string;
  emptyMessage?: string;
  items: TItem[];
  getItemName: (item: TItem) => string;
  getItemSecondary?: (item: TItem) => React.ReactNode;
  emptyForm: () => TForm;
  itemToForm: (item: TItem) => TForm;
  /** Return an error message to block saving, or null to allow it. */
  validate?: (form: TForm) => string | null;
  renderFormFields: (form: TForm, setForm: React.Dispatch<React.SetStateAction<TForm>>) => React.ReactNode;
  onSave: (form: TForm, editingId: string | null) => void;
  onDelete: (id: string) => void;
  deleteMessage?: (item: TItem) => string;
}

export function CustomContentManager<TItem extends { id: string }, TForm>({
  itemLabel,
  emptyMessage,
  items,
  getItemName,
  getItemSecondary,
  emptyForm,
  itemToForm,
  validate,
  renderFormFields,
  onSave,
  onDelete,
  deleteMessage,
}: CustomContentManagerProps<TItem, TForm>) {
  const [view, setView] = React.useState<'list' | 'form'>('list');
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<TForm>(emptyForm);
  const [error, setError] = React.useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<TItem | null>(null);

  const backToList = () => {
    setView('list');
    setEditingId(null);
    setForm(emptyForm());
    setError(null);
  };

  const handleSave = () => {
    const validationError = validate?.(form) ?? null;
    if (validationError) {
      setError(validationError);
      return;
    }
    onSave(form, editingId);
    backToList();
  };

  if (view === 'form') {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
          <IconButton size="small" onClick={backToList} aria-label="Back to list">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6">{editingId ? `Edit ${itemLabel}` : `New ${itemLabel}`}</Typography>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {renderFormFields(form, setForm)}
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={backToList}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editingId ? 'Update' : 'Create'}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingId(null); setForm(emptyForm()); setError(null); setView('form'); }}>
          Add {itemLabel}
        </Button>
      </Box>
      {items.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
          <Typography>{emptyMessage ?? `No custom ${itemLabel.toLowerCase()}s yet.`}</Typography>
        </Box>
      ) : (
        <List dense disablePadding>
          {items.map((item) => (
            <ListItem
              key={item.id}
              divider
              sx={{ pr: 10 }}
              secondaryAction={
                <Stack direction="row" gap={0.5}>
                  <IconButton size="small" aria-label={`Edit ${getItemName(item)}`} onClick={() => { setEditingId(item.id); setForm(itemToForm(item)); setError(null); setView('form'); }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" aria-label={`Delete ${getItemName(item)}`} onClick={() => setDeleteTarget(item)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              }
            >
              <ListItemText primary={getItemName(item)} secondary={getItemSecondary?.(item)} />
            </ListItem>
          ))}
        </List>
      )}

      <ConfirmActionDialog
        open={deleteTarget !== null}
        title={`Delete ${itemLabel}`}
        message={deleteTarget
          ? deleteMessage?.(deleteTarget) ?? `Delete "${getItemName(deleteTarget)}"? This cannot be undone.`
          : ''}
        confirmLabel="Delete"
        confirmColor="error"
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) onDelete(deleteTarget.id);
          setDeleteTarget(null);
        }}
      />
    </Box>
  );
}
